import { useMutation, useQueryClient } from 'react-query';

import { apiClient } from 'src/services/api/client';
import { queryKeys } from 'src/services/api/constants';
import { GET_LEADS_PATH } from 'src/services/api/leads/constants';
import { getExtractFactsQueryKey } from 'src/services/api/leads/use-extract-facts';
import type { ILead } from 'src/services/api/leads/types';

/*
 * A partial set of smart fields to change — only the keys being updated, NOT the whole
 * blob. The hook merges them over the freshest cached fields, so callers must never spread
 * a stale render snapshot in (that would roll back changes another mutation just made).
 */
export type SmartFieldsPayload = Record<string, string | Record<string, string>>;

type UpdateSmartFieldsResponse = {
    phone_number: string | null;
    email: string | null;
    smart_fields_data: SmartFieldsPayload | null;
};

/*
 * Contact facts live on the lead's own columns, so they are sent as their own
 * fields rather than inside the smart-fields blob. An omitted contact field leaves
 * the column unchanged on the server.
 */
type ReplaceSmartFieldsRequest = {
    smart_fields_data: SmartFieldsPayload;
    phone_number?: string;
    email?: string;
};

const mergeSmartFields = (lead: ILead, changed: SmartFieldsPayload): ILead => ({
    ...lead,
    smart_fields_data: { ...(lead.smart_fields_data ?? {}), ...changed },
});

export const useUpdateSmartFields = (leadUuid: string) => {
    const queryClient = useQueryClient();
    const leadKey = [queryKeys.leads, leadUuid];

    return useMutation(
        async (changed: SmartFieldsPayload) => {
            /*
             * PUT the changed keys merged over the freshest cached blob, with the
             * contact facts split out into their own request fields.
             */
            const cached = queryClient.getQueryData<ILead>(leadKey);
            const { phone_number: phoneNumber, email, ...smartFieldsData } = {
                ...(cached?.smart_fields_data ?? {}),
                ...changed,
            };
            const body: ReplaceSmartFieldsRequest = { smart_fields_data: smartFieldsData };
            if (phoneNumber !== undefined) {
                body.phone_number = phoneNumber as string;
            }
            if (email !== undefined) {
                body.email = email as string;
            }
            return apiClient(`${GET_LEADS_PATH}/${leadUuid}/smart-fields`, {
                method: 'put',
                json: body,
            }).json<UpdateSmartFieldsResponse>();
        },
        {
            /*
             * Apply the change to the cache up front so a rapid follow-up mutation merges
             * over it (not a pre-change snapshot) and can't roll this one back.
             */
            onMutate: (changed) => {
                const old = queryClient.getQueryData<ILead>(leadKey);
                if (old) {
                    queryClient.setQueryData<ILead>(leadKey, mergeSmartFields(old, changed));
                }
            },
            onSuccess: (res) => {
                /* Reconcile with the server's result (contact-column routing, coupling, ...). */
                const old = queryClient.getQueryData<ILead>(leadKey);
                if (old) {
                    queryClient.setQueryData<ILead>(leadKey, {
                        ...mergeSmartFields(old, res.smart_fields_data ?? {}),
                        phone_number: res.phone_number,
                        email: res.email,
                    });
                }
                queryClient.invalidateQueries(getExtractFactsQueryKey(leadUuid));
                queryClient.invalidateQueries(queryKeys.leads);
            },
        }
    );
};
